import React, { useEffect, useState } from 'react';
import { useSortBy, useTable } from 'react-table';

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import HistoryIcon from "@mui/icons-material/History";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

import { TooltipManage } from "./TooltipManage";
import { FEATURES } from '../../../../config/features';

export function CreateTable({ dataList = [],
  dataHeader = [],
  dataAccessor = [],
  editHandleClick,
  viewEmployeeTransactionHistoryHandleClick,
  employees,
  setSelectedEmployee,
  selectedToApprove,
  setSelectedToApprove,
  currentPage, setCurrentPage,
  isSelectAll, setIsSelectAll, activeRowIds, setActiveRowIds
}) {
  const itemsPerPage = 7;

  const columns = React.useMemo(() =>
    dataHeader.map((header, index) => ({
      Header: header,
      accessor: dataAccessor[index]
    })),
    [dataHeader, dataAccessor]
  );

  const [sortHeaderValue, setSortHeaderValue] = useState({ id: "", desc: false, isDefault: true });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy
  } = useTable(
    {
      columns,
      data: dataList,
      initialState: {
        sortBy: sortHeaderValue.isDefault
          ? []  // No sorting if it's default
          : [{ id: sortHeaderValue.id, desc: sortHeaderValue.desc }] // Apply sorting if not default
      }
    },
    useSortBy
  );

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, rows.length);
  const currentItems = rows.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const isLastPage = currentPage === totalPages;

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    if (isSelectAll) {
      const newActiveRowIds = rows.map(item => item.original.id);
      const newActiveRowOriginal = rows.map(item => item.original);
      setActiveRowIds(newActiveRowIds);
      setSelectedEmployee(newActiveRowOriginal);
      setIsSelectAll(false);
    }
  }, [isSelectAll, setIsSelectAll, setSelectedEmployee, rows, setActiveRowIds])

  const handleRowClick = (rowData) => {
    setSelectedEmployee(prevSelected => {
      const isAlreadySelected = prevSelected.some(item => item.id === rowData.id);
      return isAlreadySelected
        ? prevSelected.filter(item => item.id !== rowData.id)  // Remove rowData if already selected
        : [...prevSelected, rowData];                         // Add rowData if not selected
    });
    setActiveRowIds(prevIds =>
      prevIds.includes(rowData.id)
        ? prevIds.filter(id => id !== rowData.id)  // Remove the row ID if it is already active
        : [...prevIds, rowData.id]                 // Add the new row ID if it is not already active
    );
  };

  //Sorting Feature
  const sortState = [
    { id: 0, state: "default", textValue: "" },
    { id: 1, state: "ascending", textValue: "↑" },
    { id: 2, state: "descending", textValue: "↓" }
  ];

  const headerClick = (column) => {
    if (sortHeaderValue.id !== column.id) {
      // New column selected, start with ascending order
      setSortHeaderValue({ id: column.id, desc: false, isDefault: false });
      setSortBy([{ id: column.id, desc: false }]);
    } else {
      // Toggle between sorting states (default -> ascending -> descending -> default)
      if (sortHeaderValue.isDefault) {
        setSortHeaderValue({ id: column.id, desc: false, isDefault: false }); // Ascending
        setSortBy([{ id: column.id, desc: false }]);
      } else if (!sortHeaderValue.desc) {
        setSortHeaderValue({ id: column.id, desc: true, isDefault: false }); // Descending
        setSortBy([{ id: column.id, desc: true }]);
      } else {
        setSortHeaderValue({ id: "", desc: false, isDefault: true }); // Default (no sorting)
        setSortBy([]); // Clear sorting
      }
    }
  };

  // Ensure sorting is reapplied on dataList reload
  useEffect(() => {
    if (!sortHeaderValue.isDefault && sortHeaderValue.id) {
      setSortBy([{ id: sortHeaderValue.id, desc: sortHeaderValue.desc }]);
    }
  }, [dataList, setSortBy, sortHeaderValue]);

  return (
    <div>
      <table {...getTableProps()} className='border-collapse'>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  onClick={() => headerClick(column)}
                  className="sortable-header"
                >
                  {`${column.render("Header")} ${sortHeaderValue.id === column.id
                    ? sortState[sortHeaderValue.isDefault ? 0 : sortHeaderValue.desc ? 2 : 1].textValue
                    : ""
                    }`}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {dataList.length <= 0 &&
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No data to display
              </td>
            </tr>
          }
          {currentItems.map((row) => {
            prepareRow(row)
            return (
              employees?.length > 0 ?
                <tr key={row.original.id}
                  {...row.getRowProps()}
                  onClick={() => handleRowClick(row.original)} // Attach onClick event handler to the row
                  className={activeRowIds.includes(row.original.id) ? 'active' : ''}
                >
                  {row.cells.map((cell) => (
                    <SetInfo header={cell.render("Cell").props.column.Header}
                      _cell={cell}
                      editHandleClick={editHandleClick}
                      viewEmployeeTransactionHistoryHandleClick={viewEmployeeTransactionHistoryHandleClick}
                      selectedToApprove={selectedToApprove}
                      setSelectedToApprove={setSelectedToApprove}
                    />
                  ))}
                </tr>
                :
                <></>
            )
          })}
        </tbody>
      </table>
      {rows?.length > itemsPerPage && employees?.length > 0 ? (
        <div>
          <div className='table-page row' style={{ alignItems: "center", justifyContent: "end", padding: "20px 0 0 0" }}>
            <button className='prev row' onClick={() => paginate(1)} disabled={currentPage === 1}
              style={{ visibility: (currentPage === 1) ? 'hidden' : 'visible', marginRight: "5px", height: "25px" }}>
              &lt;&lt;
            </button>
            <button className='prev row' onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}
              style={{ visibility: (currentPage === 1) ? 'hidden' : 'visible', marginRight: "5px", height: "25px" }}>
              <NavigateBeforeIcon style={{ marginRight: "5px" }} /> PREVIOUS
            </button>
            <button className='next row' onClick={() => paginate(currentPage + 1)} disabled={indexOfLastItem >= rows.length}
              style={{ visibility: (rows.length > itemsPerPage && !isLastPage) ? 'visible' : 'hidden', marginRight: "5px", height: "25px" }}>
              NEXT <NavigateNextIcon style={{ marginLeft: "5px" }} />
            </button>
            <button className='next row' onClick={() => paginate(totalPages)} disabled={indexOfLastItem >= rows.length}
              style={{ visibility: (rows.length > itemsPerPage && !isLastPage) ? 'visible' : 'hidden', height: "25px" }}>
              &gt;&gt;
            </button>
          </div>
          {`${currentPage}/${totalPages}`}
        </div>
      ) :
        <></>}
    </div>
  )
}
function SetInfo({ header,
  _cell,
  editHandleClick,
  viewEmployeeTransactionHistoryHandleClick,
  selectedToApprove,
  setSelectedToApprove
}) {
  const data = _cell.render("Cell").props.row.original;
  const value = _cell.render("Cell").props.value;

  const [isChecked, setIsChecked] = useState(false);

  const handleItem = (event) => {
    const checked = event.target.checked;
    setIsChecked(checked);

    if (checked) {
      setSelectedToApprove(prevItems => [...prevItems, data]);
    } else {
      setSelectedToApprove(prevItems => prevItems.filter(i => i !== data));
    }
  };

  if (header === "Photo") {
    if (data.photo) {
      const imageSrc = 'data:image/jpeg;base64,' + data.photo;

      return (
        <td>
          <img className="avatarImg" src={imageSrc} alt="" />
        </td>
      )
    }
    return (
      <td>
        <img className="avatarImg" src="images/sample.jpg" alt="" />
      </td>
    )
  }

  if (header === "Is Approved?") {
    if (value === "Approved") {
      return (
        <td>
          <input type="checkbox"
            id="overtime-allowance"
            className="checkbox"
            name=""
            checked={true}
            disabled
          />
        </td>
      )
    }

    if (!value) {
      return (
        <td>
          <input type="checkbox"
            id="overtime-allowance"
            className="checkbox"
            name=""
            checked={isChecked}
            onChange={handleItem}
            disabled
          />
        </td>
      )
    }

    return (
      <td>
        {_cell.render("Cell").props.value}
      </td>
    )
  }

  if (header === "") {
    return (
      <td>
        <div className="row" style={{ alignItems: "center", justifyContent: "center" }}>
          <TooltipManage text="View">
            <div style={{ padding: "0 5px" }} onClick={() => editHandleClick(data)}><RemoveRedEyeIcon /></div>
          </TooltipManage>
          {FEATURES.ALLOW_EMPLOYEE_TRANSACTION_MODULE === true && (
            <TooltipManage text="View History">
              <div style={{ padding: "0 5px" }} onClick={() => viewEmployeeTransactionHistoryHandleClick(data)}><HistoryIcon /></div>
            </TooltipManage>
          )}
        </div>
      </td>
    )
  }

  if (header === "Date Created"
  ) {
    if (!value) {
      return (
        <td>
        </td>
      );
    }

    const date = new Date(_cell.render("Cell").props.value);
    if (!date) {
      return (
        <td>
          null
        </td>
      );
    }

    const mm = date.getMonth() + 1; // Adding 1 because getMonth returns 0-indexed months
    const dd = date.getDate();
    const yyyy = date.getFullYear();

    const formattedDateFrom = `${mm}/${dd}/${yyyy}`;

    return (
      <td>
        {formattedDateFrom}
      </td>
    )
  }

  return (
    <td {..._cell.getCellProps()}> {_cell.render("Cell")}</td>
  )
}